import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  UseDisclosureReturn,
  useToast,
  VStack,
  HStack,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Divider,
} from '@chakra-ui/react';
import { Labeled } from '@partiu/ui/components/molecules/Labeled.tsx';
import { useForm } from 'react-hook-form';
import { useAgreementLandingInfo, useUpdateAgreementLandingInfoMutation } from '@partiu/api/dashboard/hooks.ts';
import { useEffect, useState } from 'react';
import { Input, PhoneInput } from '@partiu/ui/components/atoms/Input.tsx';
import { toPhone } from '@partiu/ui/formatters.ts';
import { IAgreementLanding } from '@partiu/api/dashboard/types.ts';
import { FaCog, FaSave, FaTimes } from 'react-icons/fa';

export function ConfigurationsModal({ isOpen, onClose }: UseDisclosureReturn) {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading, error } = useAgreementLandingInfo();

  const { register, reset, handleSubmit, formState: { errors, isDirty } } = useForm({
    defaultValues: {
      email: '',
      telephone: '',
      cellphone: '',
      contactLink: '',
      footerText: '',
    }
  });

  const toFormValues = (info: IAgreementLanding) => ({
    email: info.email || '',
    telephone: toPhone(info.telephone || ''),
    cellphone: toPhone(info.cellphone || ''),
    contactLink: info.contactLink || '',
    footerText: info.footerText || '',
  });

  const mutation = useUpdateAgreementLandingInfoMutation({
    onSuccess(data) {
      reset(toFormValues(data));

      toast({
        title: 'Sucesso!',
        description: 'Configurações salvas com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsSubmitting(false);
      onClose();
    },
    onError(error: unknown) {
      const errorMessage = error && typeof error === 'object' && 'response' in error
        ? (error.response as { data?: { message?: string } })?.data?.message
        : undefined;

      toast({
        title: 'Erro ao salvar',
        description: errorMessage || 'Não foi possível salvar as configurações. Tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsSubmitting(false);
    },
    onSettled() {
      setIsSubmitting(false);
    },
  });

  useEffect(() => {
    if (data && isOpen) {
      reset(toFormValues(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isOpen, reset]);

  const onSubmit = (formData: ReturnType<typeof toFormValues>) => {
    setIsSubmitting(true);
    mutation.mutate({
      email: formData.email,
      telephone: formData.telephone,
      cellphone: formData.cellphone,
      contactLink: formData.contactLink.trim(),
      footerText: formData.footerText,
    });
  };

  const handleClose = () => {
    if (isDirty && !isSubmitting) {
      if (window.confirm('Você tem alterações não salvas. Deseja realmente sair?')) {
        reset();
        onClose();
      }
    } else {
      onClose();
    }
  };

  return (
    <Modal size={'md'} isOpen={isOpen} onClose={handleClose} closeOnOverlayClick={!isSubmitting}>
      <ModalOverlay backdropFilter={'blur(8px)'} bg="blackAlpha.300" />
      <ModalContent borderRadius="xl" boxShadow="2xl">
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            <HStack spacing={3}>
              <FaCog color="#3182CE" />
              <Text fontSize="xl" fontWeight="bold">Configurações</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton isDisabled={isSubmitting} />

          <Divider />

          <ModalBody py={6}>
            {isLoading ? (
              <VStack spacing={4} py={8}>
                <Spinner size="lg" color="blue.500" />
                <Text color="gray.600">Carregando configurações...</Text>
              </VStack>
            ) : error ? (
              <Alert status="error" borderRadius="lg">
                <AlertIcon />
                <Box>
                  <AlertTitle>Erro ao carregar!</AlertTitle>
                  <AlertDescription>
                    Não foi possível carregar as configurações. Tente novamente.
                  </AlertDescription>
                </Box>
              </Alert>
            ) : (
              <VStack spacing={6}>
                <Labeled label="Email de Contato">
                  <Input
                    {...register('email', {
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Email inválido'
                      }
                    })}
                    placeholder="contato@empresa.com"
                    isInvalid={!!errors.email}
                  />
                  {errors.email && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.email.message as string}
                    </Text>
                  )}
                </Labeled>

                <Labeled label="Número de Telefone">
                  <PhoneInput {...register('telephone')} placeholder="(11) 3333-4444 ou 0800 600 9988" />
                </Labeled>

                <Labeled label="Celular / WhatsApp">
                  <PhoneInput
                    {...register('cellphone')}
                    placeholder="(11) 9 9999-9999"
                    isDisabled={isLoading || isSubmitting}
                  />
                </Labeled>

                <Labeled label="Link de redirecionamento (opcional)" helpText="Tem prioridade sobre o WhatsApp quando preenchido.">
                  <Input
                    {...register('contactLink')}
                    placeholder="https://wa.me/5511999999999 ou link de outro canal"
                    type="url"
                  />
                </Labeled>

                <Labeled label="Texto do Rodapé">
                  <Input
                    {...register('footerText')}
                    placeholder="Texto que aparecerá no rodapé do site"
                  />
                </Labeled>
              </VStack>
            )}
          </ModalBody>

          <Divider />

          <ModalFooter>
            <Spacer />
            <Button
              onClick={handleClose}
              variant="ghost"
              mr={3}
              leftIcon={<FaTimes />}
              isDisabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              isLoading={isSubmitting}
              loadingText="Salvando..."
              colorScheme="blue"
              leftIcon={<FaSave />}
              isDisabled={isLoading || !!error}
            >
              Salvar Configurações
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
